/**
 * Bankruptcy Dispute Page - Step 9 of Dispute Wizard
 * 
 * Features:
 * - Bankruptcy information collection form
 * - Two letter types: Standard Dispute + Enhanced FCRA Letter
 * - Bureau selection (Experian, Equifax, TransUnion)
 * - Integration with final dispute package
 * - Client signature support
 */

import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Scale,
  FileText,
  Download,
  CheckCircle2,
  AlertCircle,
  Info,
  Building2,
  Calendar,
  Hash,
  Loader2,
  FileCheck,
  XCircle,
  ChevronRight,
  Mail
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Bureau options
const BUREAUS = [
  { id: 'experian', name: 'Experian', color: 'bg-blue-600' },
  { id: 'equifax', name: 'Equifax', color: 'bg-red-600' },
  { id: 'transunion', name: 'TransUnion', color: 'bg-cyan-600' }
];

// Bankruptcy types
const BANKRUPTCY_TYPES = [
  { id: 'chapter7', name: 'Chapter 7' },
  { id: 'chapter13', name: 'Chapter 13' },
  { id: 'chapter11', name: 'Chapter 11' },
  { id: 'unknown', name: 'Unknown/Not Sure' }
];

export default function BankruptcyDisputePage({
  clientId,
  clientForm,
  hasBankruptcy,
  setHasBankruptcy,
  bankruptcyLetterGenerated,
  setBankruptcyLetterGenerated,
  signatureData,
  onStepComplete,
  selectedBureaus = ['experian', 'equifax', 'transunion']
}) {
  // Bankruptcy info state
  const [bankruptcyInfo, setBankruptcyInfo] = useState({
    type: 'chapter7',
    caseNumber: '',
    filingDate: '',
    courtName: '',
    dischargeDate: ''
  });
  
  // Letter generation state
  const [generatedLetters, setGeneratedLetters] = useState({
    standard: {},
    enhanced: {}
  });
  
  const [generating, setGenerating] = useState({});
  const [includeInPackage, setIncludeInPackage] = useState({
    standard: true,
    enhanced: true
  });

  // Check if all letters for selected bureaus have been generated
  const allStandardGenerated = selectedBureaus.every(b => generatedLetters.standard[b]);
  const allEnhancedGenerated = selectedBureaus.every(b => generatedLetters.enhanced[b]);

  useEffect(() => {
    // Mark step complete when bankruptcy is not applicable or letters are generated
    if (hasBankruptcy === false) {
      onStepComplete?.(true);
    } else if (hasBankruptcy === true && (allStandardGenerated || allEnhancedGenerated)) {
      setBankruptcyLetterGenerated?.(true);
      onStepComplete?.(true);
    }
  }, [hasBankruptcy, allStandardGenerated, allEnhancedGenerated]);

  // Generate standard bankruptcy dispute letter
  const generateStandardLetter = async (bureau) => {
    setGenerating(prev => ({ ...prev, [`standard_${bureau}`]: true }));
    
    try {
      const response = await axios.post(
        `${API}/api/dispute-package/bankruptcy-removal-letter`,
        {
          client_id: clientId,
          bureau: bureau,
          bankruptcy_type: BANKRUPTCY_TYPES.find(t => t.id === bankruptcyInfo.type)?.name || 'Chapter 7',
          case_number: bankruptcyInfo.caseNumber || null,
          filing_date: bankruptcyInfo.filingDate || null,
          court_name: bankruptcyInfo.courtName || null,
          signature_data: signatureData || null
        },
        { responseType: 'blob' }
      );
      
      // Download the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const clientName = (clientForm?.name || 'Consumer').replace(/\s+/g, '_');
      link.href = url;
      link.download = `Bankruptcy_Dispute_Standard_${bureau.toUpperCase()}_${clientName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setGeneratedLetters(prev => ({
        ...prev,
        standard: { ...prev.standard, [bureau]: true }
      }));
      
      toast.success(`${bureau.toUpperCase()} standard bankruptcy letter generated!`);
    } catch (error) {
      console.error('Error generating standard letter:', error);
      toast.error(`Failed to generate ${bureau} letter`);
    } finally {
      setGenerating(prev => ({ ...prev, [`standard_${bureau}`]: false }));
    }
  };

  // Generate enhanced FCRA bankruptcy dispute letter (new letter based on user's template)
  const generateEnhancedLetter = async (bureau) => {
    setGenerating(prev => ({ ...prev, [`enhanced_${bureau}`]: true }));
    
    try {
      const response = await axios.post(
        `${API}/api/dispute-package/bankruptcy-fcra-letter`,
        {
          client_id: clientId,
          bureau: bureau,
          bankruptcy_type: BANKRUPTCY_TYPES.find(t => t.id === bankruptcyInfo.type)?.name || 'Chapter 7',
          case_number: bankruptcyInfo.caseNumber || null,
          filing_date: bankruptcyInfo.filingDate || null,
          court_name: bankruptcyInfo.courtName || null,
          signature_data: signatureData || null
        },
        { responseType: 'blob' }
      );
      
      // Download the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const clientName = (clientForm?.name || 'Consumer').replace(/\s+/g, '_');
      link.href = url;
      link.download = `Bankruptcy_FCRA_Dispute_${bureau.toUpperCase()}_${clientName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setGeneratedLetters(prev => ({
        ...prev,
        enhanced: { ...prev.enhanced, [bureau]: true }
      }));
      
      toast.success(`${bureau.toUpperCase()} enhanced FCRA bankruptcy letter generated!`);
    } catch (error) {
      console.error('Error generating enhanced letter:', error);
      toast.error(`Failed to generate ${bureau} enhanced letter`);
    } finally {
      setGenerating(prev => ({ ...prev, [`enhanced_${bureau}`]: false }));
    }
  };

  // Generate all letters for a bureau
  const generateAllForBureau = async (bureau) => {
    await generateStandardLetter(bureau);
    await generateEnhancedLetter(bureau);
  };

  // Generate all letters for all selected bureaus
  const generateAllLetters = async () => {
    for (const bureau of selectedBureaus) {
      await generateAllForBureau(bureau);
    }
  };

  // If user hasn't decided yet about bankruptcy
  if (hasBankruptcy === null) {
    return (
      <div className="space-y-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Scale className="w-5 h-5 text-orange-500" />
              Bankruptcy on Credit Report?
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Do you have a bankruptcy showing on your credit report that you want to dispute?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={() => setHasBankruptcy(true)}
                variant="outline"
                className="h-24 flex flex-col gap-2 bg-zinc-800 border-zinc-700 hover:bg-orange-600/20 hover:border-orange-500"
              >
                <AlertCircle className="w-8 h-8 text-orange-500" />
                <span className="text-white">Yes, I have bankruptcy to dispute</span>
              </Button>
              
              <Button
                onClick={() => setHasBankruptcy(false)}
                variant="outline"
                className="h-24 flex flex-col gap-2 bg-zinc-800 border-zinc-700 hover:bg-green-600/20 hover:border-green-500"
              >
                <CheckCircle2 className="w-8 h-8 text-green-500" />
                <span className="text-white">No bankruptcy to dispute</span>
              </Button>
            </div>
            
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-zinc-400">
                  <p className="font-medium text-zinc-300 mb-1">About Bankruptcy Disputes</p>
                  <p>
                    Federal bankruptcy courts <strong className="text-white">do not report directly</strong> to credit bureaus. 
                    This means the bankruptcy on your report may be inaccurate or unverifiable. 
                    Our letters demand verification under the Fair Credit Reporting Act (FCRA).
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If user has no bankruptcy
  if (hasBankruptcy === false) {
    return (
      <div className="space-y-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              No Bankruptcy to Dispute
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-zinc-400">
              Great! Since you don't have a bankruptcy to dispute, you can proceed to the next step.
            </p>
            <Button
              onClick={() => setHasBankruptcy(null)}
              variant="outline"
              className="mt-4 border-zinc-700 text-zinc-400 hover:text-white"
            >
              Change Selection
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main bankruptcy dispute form
  return (
    <div className="space-y-6">
      {/* Bankruptcy Information */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Scale className="w-5 h-5 text-orange-500" />
            Bankruptcy Information
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Enter any details you know about the bankruptcy (all fields optional)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Bankruptcy Type */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Bankruptcy Type</Label>
              <select
                value={bankruptcyInfo.type}
                onChange={(e) => setBankruptcyInfo(prev => ({ ...prev, type: e.target.value }))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {BANKRUPTCY_TYPES.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            
            {/* Case Number */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Hash className="w-4 h-4" />
                Case Number (if known)
              </Label>
              <Input
                value={bankruptcyInfo.caseNumber}
                onChange={(e) => setBankruptcyInfo(prev => ({ ...prev, caseNumber: e.target.value }))}
                placeholder="e.g., 23-12345"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            {/* Filing Date */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Filing Date (if known)
              </Label>
              <Input
                type="date"
                value={bankruptcyInfo.filingDate}
                onChange={(e) => setBankruptcyInfo(prev => ({ ...prev, filingDate: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            {/* Court Name */}
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Court Name (if known)
              </Label>
              <Input
                value={bankruptcyInfo.courtName}
                onChange={(e) => setBankruptcyInfo(prev => ({ ...prev, courtName: e.target.value }))}
                placeholder="e.g., U.S. Bankruptcy Court - Southern District"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>
          
          <Button
            onClick={() => setHasBankruptcy(null)}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-400 hover:text-white"
          >
            <XCircle className="w-4 h-4 mr-2" />
            I don't have bankruptcy to dispute
          </Button>
        </CardContent>
      </Card>

      {/* Letter Types Explanation */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">Available Dispute Letters</CardTitle>
          <CardDescription className="text-zinc-400">
            Two powerful letter templates to dispute bankruptcy records
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Standard Letter */}
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-blue-400" />
                <h4 className="font-medium text-white">Standard Dispute Letter</h4>
              </div>
              <p className="text-sm text-zinc-400 mb-3">
                Traditional FCRA dispute letter demanding investigation and verification of the bankruptcy record.
              </p>
              <Badge variant="outline" className="text-blue-400 border-blue-400/30">
                FCRA §611 Dispute
              </Badge>
            </div>
            
            {/* Enhanced FCRA Letter */}
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-orange-600/30">
              <div className="flex items-center gap-2 mb-2">
                <FileCheck className="w-5 h-5 text-orange-400" />
                <h4 className="font-medium text-white">Enhanced FCRA Letter</h4>
                <Badge className="bg-orange-600 text-white text-xs">NEW</Badge>
              </div>
              <p className="text-sm text-zinc-400 mb-3">
                Comprehensive letter citing that U.S. Bankruptcy Courts do not furnish information to credit bureaus. 
                Demands removal under §1681e(b) and §1681i.
              </p>
              <Badge variant="outline" className="text-orange-400 border-orange-400/30">
                FCRA §1681e(b) + §1681i
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Generate Letters by Bureau */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">Generate Bankruptcy Dispute Letters</CardTitle>
          <CardDescription className="text-zinc-400">
            Generate letters for each credit bureau reporting the bankruptcy
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {selectedBureaus.map(bureau => {
            const bureauInfo = BUREAUS.find(b => b.id === bureau);
            const standardDone = generatedLetters.standard[bureau];
            const enhancedDone = generatedLetters.enhanced[bureau];
            const generatingStandard = generating[`standard_${bureau}`];
            const generatingEnhanced = generating[`enhanced_${bureau}`];
            
            return (
              <div key={bureau} className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${bureauInfo?.color || 'bg-zinc-500'}`} />
                    <span className="font-medium text-white">{bureauInfo?.name || bureau}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {standardDone && (
                      <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/30">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Standard
                      </Badge>
                    )}
                    {enhancedDone && (
                      <Badge className="bg-orange-600/20 text-orange-400 border-orange-600/30">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Enhanced
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => generateStandardLetter(bureau)}
                    disabled={generatingStandard}
                    variant="outline"
                    size="sm"
                    className={`border-blue-600/50 ${standardDone ? 'bg-blue-600/20 text-blue-400' : 'text-blue-400 hover:bg-blue-600/20'}`}
                  >
                    {generatingStandard ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    {standardDone ? 'Re-download Standard' : 'Standard Letter'}
                  </Button>
                  
                  <Button
                    onClick={() => generateEnhancedLetter(bureau)}
                    disabled={generatingEnhanced}
                    variant="outline"
                    size="sm"
                    className={`border-orange-600/50 ${enhancedDone ? 'bg-orange-600/20 text-orange-400' : 'text-orange-400 hover:bg-orange-600/20'}`}
                  >
                    {generatingEnhanced ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    {enhancedDone ? 'Re-download Enhanced' : 'Enhanced FCRA Letter'}
                  </Button>
                  
                  <Button
                    onClick={() => generateAllForBureau(bureau)}
                    disabled={generatingStandard || generatingEnhanced}
                    className="bg-zinc-700 hover:bg-zinc-600 text-white"
                    size="sm"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Generate Both
                  </Button>
                </div>
              </div>
            );
          })}
          
          {/* Generate All Button */}
          <div className="pt-4 border-t border-zinc-700">
            <Button
              onClick={generateAllLetters}
              disabled={Object.values(generating).some(v => v)}
              className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white"
            >
              {Object.values(generating).some(v => v) ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Generate All Bankruptcy Letters for All Bureaus
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Include in Package Options */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">Include in Final Dispute Package</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeInPackage.standard}
                onChange={(e) => setIncludeInPackage(prev => ({ ...prev, standard: e.target.checked }))}
                className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-zinc-300">Include Standard Dispute Letters in final package</span>
            </label>
            
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeInPackage.enhanced}
                onChange={(e) => setIncludeInPackage(prev => ({ ...prev, enhanced: e.target.checked }))}
                className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-zinc-300">Include Enhanced FCRA Letters in final package</span>
            </label>
          </div>
          
          <div className="mt-4 bg-zinc-800/50 rounded-lg p-3 border border-zinc-700">
            <p className="text-sm text-zinc-400">
              <Info className="w-4 h-4 inline mr-2 text-blue-400" />
              The selected letters will be automatically added to your dispute package when you reach the Mail step.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Courthouse Email Card */}
      <Card className="bg-zinc-900 border-purple-600/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-purple-400" />
            Send Courthouse Instructions to Client
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Email your client instructions on how to request a letter from the bankruptcy court
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
            <p className="text-sm text-purple-300">
              <strong>Why this matters:</strong> Federal bankruptcy courts do NOT report directly to credit bureaus. 
              Getting written confirmation of this fact from the court clerk strengthens the dispute significantly.
            </p>
          </div>
          
          <Button
            onClick={async () => {
              const clientEmail = clientForm?.email;
              if (!clientEmail) {
                toast.error('Client email is required. Please update client information.');
                return;
              }
              
              try {
                const response = await axios.post(`${API}/api/dispute-package/bankruptcy-courthouse-email`, {
                  client_id: clientId,
                  client_email: clientEmail,
                  court_name: bankruptcyInfo.courtName || '',
                  case_number: bankruptcyInfo.caseNumber || '',
                  filing_date: bankruptcyInfo.filingDate || ''
                });
                
                if (response.data.success) {
                  toast.success('Courthouse instructions sent to client!');
                } else {
                  toast.error('Failed to send email');
                }
              } catch (error) {
                console.error('Error sending courthouse email:', error);
                toast.error('Failed to send courthouse instructions');
              }
            }}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
          >
            <Mail className="w-4 h-4 mr-2" />
            Email Courthouse Request Instructions to Client
          </Button>
          
          <p className="text-xs text-zinc-500 mt-3 text-center">
            This will send detailed instructions on requesting a letter from the court confirming they don't report to credit bureaus.
          </p>
        </CardContent>
      </Card>

      {/* Progress Summary */}
      {(allStandardGenerated || allEnhancedGenerated) && (
        <Card className="bg-green-900/20 border-green-600/30">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-500" />
              <div>
                <p className="text-green-400 font-medium">Bankruptcy letters generated!</p>
                <p className="text-sm text-green-400/70">You can proceed to the next step.</p>
              </div>
              <ChevronRight className="w-5 h-5 text-green-500 ml-auto" />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
